/**
 * Modello delle **proposte** (suggerimenti) delle superfici SLA (piano Client
 * sez. 5 F2c, sez. 8 riga 123). Due superfici lo consumano: l'editor del modello
 * (`SlaModelEditor`) e l'avvio processo (`SlaStartSection`).
 *
 * ── Cosa e' codificato qui ───────────────────────────────────────────────────
 * Le tre garanzie di F2c, in un solo posto:
 * 1. **mai applicate silenziosamente**: una proposta non scrive nulla. L'unico
 *    punto di scrittura e' `applySlaSuggestion`, che il componente chiama **solo**
 *    dal comando esplicito di conferma. Nessun effetto, nessuna derivazione e
 *    nessun salvataggio la invoca: non esiste un percorso in cui una risposta di
 *    proposta finisca nella bozza senza un click dell'utente.
 * 2. **stima/cold-start dichiarati**: `estimable = false` non produce un valore
 *    finto. `value` resta `null` e la superficie dichiara lo stato con
 *    `SLA_SUGGESTION_UNAVAILABLE_KEY` (chiave **esistente**, gia' usata dalle
 *    fixture C1: `slaFixtures.ts` `SLA_FIXTURE_PREVIEW_NOT_ESTIMABLE.warnings`).
 * 3. **errore della sorgente non impedisce un flusso valido**: una sorgente non
 *    raggiungibile e' uno stato della superficie (`source_error`), non un errore
 *    che blocca. La bozza e il salvataggio/avvio non dipendono dalle proposte.
 *
 * ── Sorgente delle proposte: contratto mancante, dichiarato ───────────────────
 * L'unica sorgente che **esiste** oggi e' la stima del Server
 * (`POST /sla/organizations/{org}/preview`, `slaApi.ts:257-264`):
 * `slaSuggestionFromPreview` la adatta. Non esiste alcuna API di proposte AI nel
 * client (`src/api/aiApi.ts` e' l'istanza axios del solo backend chat `/ai`,
 * consumata da `hooks/useLLM.ts`; nessuna rotta SLA la usa) **ne' lato Server**
 * (`Flowenti-Server/TestServer/Controllers/Controller.SLA.pas:26-35`: preview,
 * definitions, instances, kpi, diagram, override, recalculate, settings,
 * preferences — nessuna rotta di suggerimenti/AI). Percio' qui **non** si
 * inventa il contratto di rete delle proposte AI: `SlaSuggestion` e' il view
 * model che la superficie rende, e un adattatore AI si aggiunge accanto a
 * `slaSuggestionFromPreview` senza toccare i componenti. Il contratto che serve
 * e' elencato nel riepilogo di F2c come **residuo bloccante**:
 * - `GET /sla/organizations/{org}/models/{model}/suggestions` (per modello), e
 * - lo stesso per l'avvio (proposta per target, con `model_revision`),
 * con risposta `{ suggestions: [{ target_type, target_designerid, field,
 * value, rationale }], meta: { model_revision, as_of } }` e la stessa semantica
 * "mai applicata dal Server: la applica l'utente".
 *
 * ── Chiavi di traduzione ─────────────────────────────────────────────────────
 * Il piano sez. 4 fa concordare le chiavi con Python P1 e il catalogo e' di
 * proprieta' Python: qui non si scrive testo e si **riusa** cio' che esiste
 * (`sla.start.preview.*`, `sla.validation.estimate_unavailable`). Le sole chiavi
 * nuove sono quelle della conferma esplicita, che oggi non hanno un equivalente
 * nel manifest: sono elencate come mancanti nel riepilogo di F2c.
 */

import type {
  SlaDurationFormat,
  SlaDurationUnit,
  SlaPreviewRequest,
  SlaPreviewResponse,
  SlaTargetType,
} from 'model/slaModel';
import {
  slaStartAbsoluteDeadlineAllowed,
  slaStartTargetKey,
  type SlaStartDraft,
} from 'model/slaStartModel';

// ─── Chiavi di traduzione ────────────────────────────────────────────────────

/**
 * Chiavi **nuove** della superficie delle proposte: il gesto di conferma
 * esplicita non ha un equivalente in `SLA_EDITOR_TEXT_KEYS` /
 * `SLA_START_TEXT_KEYS`. Da concordare con Python P1 (manifest F1).
 */
export const SLA_SUGGESTION_TEXT_KEYS = {
  /** Titolo della sezione delle proposte (distinta dal blocco della stima). */
  title: 'sla.suggestion.title',
  /** Conferma esplicita: e' l'unico modo in cui una proposta entra nella bozza. */
  apply: 'sla.suggestion.apply',
  /**
   * Marcatore dopo la conferma. Il valore entra in un campo **editabile**
   * (`absoluteDeadlineAt` della bozza di avvio): e' cosi' che la proposta e'
   * modificabile, e la modifica dell'utente non viene mai sovrascritta da una
   * proposta successiva (la bozza non e' derivata dalla stima).
   */
  applied: 'sla.suggestion.applied',
  /** Sorgente non raggiungibile: si dichiara e non blocca (F2c). */
  sourceError: 'sla.suggestion.source_error',
} as const;

/**
 * Stato "stima non disponibile / cold start". **Chiave esistente**: e' quella
 * che le fixture C1 usano per una preview non stimabile
 * (`src/api/slaFixtures.ts`, `SLA_FIXTURE_PREVIEW_NOT_ESTIMABLE`), quindi non se
 * ne introduce una nuova. Non e' ancora nel manifest editor
 * (`SLA_EDITOR_MANIFEST_KEYS`): va aggiunta li' da P1.
 */
export const SLA_SUGGESTION_UNAVAILABLE_KEY = 'sla.validation.estimate_unavailable';

// ─── Proposta ────────────────────────────────────────────────────────────────

/**
 * Tipo di proposta resa oggi. `absolute_deadline` e' l'unico valore di stima che
 * il contratto esistente offre: `SlaPreviewResponse.deadline_at`. Altri tipi
 * (durata suggerita, soglia, responsabile) **non** sono rappresentabili senza il
 * contratto di proposte AI dichiarato sopra, quindi non vengono inventati.
 */
export type SlaSuggestionKind = 'absolute_deadline';

/**
 * Proposta per un target. View model della UI: non e' un DTO e non ha un
 * payload di rete proprio (vedi l'intestazione del file).
 */
export interface SlaSuggestion {
  /** Target a cui la proposta si riferisce (`slaStartTargetKey`). */
  targetKey: string;
  kind: SlaSuggestionKind;
  /**
   * Valore proposto, ISO 8601 **UTC**. `null` quando la stima non e'
   * disponibile: mai un numero o un istante inventato.
   */
  value: string | null;
  /** false = stima non disponibile (cold start, attese non stimabili). */
  estimable: boolean;
  /** Istante della stima, dichiarato dalla sorgente. */
  asOf: string | null;
  /** Revisione su cui la stima e' stata calcolata: una stima di un'altra revisione non si applica. */
  modelRevision: number | null;
  /** Messaggi dichiarati dalla sorgente, resi come sono (non tradotti). */
  warnings: string[];
}

/**
 * Adattatore della **sola** sorgente esistente: la stima del Server. `value`
 * resta `null` se la stima non e' disponibile, anche se `deadline_at` porta una
 * stringa: `estimable` e' la dichiarazione autorevole.
 */
export const slaSuggestionFromPreview = (
  targetKey: string,
  preview: SlaPreviewResponse,
  modelRevision: number | null,
): SlaSuggestion => ({
  targetKey,
  kind: 'absolute_deadline',
  value: preview.estimable && preview.deadline_at ? preview.deadline_at : null,
  estimable: preview.estimable,
  asOf: preview.as_of || null,
  modelRevision,
  warnings: Array.isArray(preview.warnings) ? preview.warnings : [],
});

/** true quando la proposta porta un valore: la stima e' disponibile. */
export const isSlaSuggestionAvailable = (suggestion: SlaSuggestion): boolean =>
  suggestion.estimable && suggestion.value !== null;

/**
 * true quando la proposta e' applicabile **ora**: la stima esiste e la revisione
 * su cui e' stata calcolata e' quella a schermo. Una stima di una revisione
 * precedente (o risolta quando la revisione non era nota) non e' la proposta
 * corrente e non si applica, come le risposte fuori ordine della preview.
 */
export const isSlaSuggestionApplicable = (
  suggestion: SlaSuggestion,
  modelRevision: number | null,
): boolean =>
  isSlaSuggestionAvailable(suggestion) &&
  (modelRevision === null ||
    suggestion.modelRevision === null ||
    suggestion.modelRevision === modelRevision);

/**
 * Applicazione **esplicita** di una proposta a una bozza di **avvio**. Pura:
 * restituisce una bozza nuova, non muta l'argomento e non scrive su nessuna API.
 *
 * Non applica quando:
 * - il target della proposta non e' quello della bozza;
 * - la stima non e' disponibile o e' di un'altra revisione;
 * - il target non ammette la deadline assoluta (`slaStartAbsoluteDeadlineAllowed`:
 *   solo il processo, sez. 5 F2b) — sul target attivita' il campo non e'
 *   rappresentabile e non si inventa un campo alternativo.
 */
export const applySlaSuggestion = (
  draft: SlaStartDraft,
  suggestion: SlaSuggestion,
  modelRevision: number | null,
): SlaStartDraft => {
  if (slaStartTargetKey(draft) !== suggestion.targetKey) return draft;
  if (!isSlaSuggestionApplicable(suggestion, modelRevision)) return draft;
  if (!slaStartAbsoluteDeadlineAllowed(draft.targetType)) return draft;
  return { ...draft, absoluteDeadlineAt: suggestion.value };
};

/**
 * true quando la bozza porta **esattamente** il valore proposto: la superficie
 * lo dichiara, e il campo resta comunque modificabile dall'utente.
 */
export const isSlaSuggestionApplied = (
  draft: SlaStartDraft,
  suggestion: SlaSuggestion,
): boolean =>
  suggestion.value !== null && draft.absoluteDeadlineAt === suggestion.value;

/** true quando il target della bozza puo' ricevere la proposta (solo processo). */
export const slaSuggestionApplySupported = (targetType: SlaTargetType): boolean =>
  slaStartAbsoluteDeadlineAllowed(targetType);

// ─── Richiesta di stima per entrambe le superfici ────────────────────────────

/** Campi della definizione che la preview consuma: comuni a bozza editor e avvio. */
export interface SlaPreviewableDraft {
  targetType: SlaTargetType;
  durationValue: number | null;
  durationUnit: SlaDurationUnit | null;
  riskRemainingPercent: number | null;
}

/**
 * Richiesta di stima da una bozza. Il **formato** e' un parametro esplicito e non
 * viene dedotto: l'avvio manda il formato letto dal Server
 * (`slaStartPreviewRequest`, sez. 5 F2b), l'editor manda sempre il relativo,
 * perche' e' quello che riscrive al salvataggio (`slaEditorModel.ts:209-223`).
 * Dedurlo qui renderebbe le due superfici intercambiabili in silenzio.
 */
export const slaPreviewRequestFromDraft = (
  draft: SlaPreviewableDraft,
  referenceAt: string,
  durationFormat: SlaDurationFormat,
): SlaPreviewRequest => ({
  reference_at: referenceAt,
  definition: {
    target_type: draft.targetType,
    duration_format: durationFormat,
    duration_value: draft.durationValue,
    duration_unit: draft.durationUnit,
    risk_remaining_percent: draft.riskRemainingPercent,
  },
});

// ─── Resa ────────────────────────────────────────────────────────────────────

/** Chiave stabile della proposta: un target, una proposta (oggi). */
export const slaSuggestionKey = (suggestion: SlaSuggestion): string =>
  `${suggestion.targetKey}:${suggestion.kind}`;
